import { useQueryClient } from "@tanstack/react-query"
import { createContext, useContext, useMemo, type PropsWithChildren, type FC, useEffect, useCallback } from "react"
import { LogoutVariables, startOAuthFlow, useAuthSession, useLogout } from "~/services/oauth"
import { queryKeys } from "~/services/query-keys.ts"
import { navigationStateStore } from "~/stores/domain"
import { User } from "~/types"
import { AuthEvents, UNAUTHORIZED_EVENT } from "~/utils"

export type AuthState = {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export type LogoutAction = (options?: LogoutVariables) => void

export type AuthActions = {
  logout: LogoutAction
}

const AuthStateContext = createContext<AuthState | undefined>(undefined)
const AuthActionsContext = createContext<AuthActions | undefined>(undefined)

export const useAuthState = (): AuthState => {
  const ctx = useContext(AuthStateContext)
  if (!ctx) {
    throw new Error("useAuthState must be used within AuthProvider")
  }
  return ctx
}

export const useAuthActions = (): AuthActions => {
  const ctx = useContext(AuthActionsContext)
  if (!ctx) {
    throw new Error("useAuthActions must be used within AuthProvider")
  }
  return ctx
}

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient()

  const { data: user, isLoading } = useAuthSession()

  const { mutateAsync: logoutAsync } = useLogout()

  // Global unauthorized handler
  useEffect(() => {
    const handleUnauthorized = async () => {
      // Clear session
      queryClient.invalidateQueries({ queryKey: queryKeys.authSession() })

      // Allow navigation even if users have unsaved change as the auth session is not valid
      navigationStateStore.setNavigatingDueToAuthError(true)

      try {
        await startOAuthFlow({ replaceCurrentHistoryEntry: true })
      } catch (error) {
        console.error(`[AuthProvider] Failed to start OAuth flow after ${UNAUTHORIZED_EVENT} event:`, error)
      }
    }

    AuthEvents.on(UNAUTHORIZED_EVENT, handleUnauthorized)

    return () => {
      AuthEvents.off(UNAUTHORIZED_EVENT, handleUnauthorized)
    }
  }, [queryClient])

  // Memoize state & actions separately
  const stateValue = useMemo<AuthState>(
    () => ({
      user: user ?? null,
      isAuthenticated: !!user,
      isLoading,
    }),
    [user, isLoading]
  )

  const logout: LogoutAction = useCallback(
    (options) => {
      // Logout on the backend side. Removes session id cookie.
      logoutAsync(options).catch((error) => {
        console.error("[AuthProvider] Logout request failed:", error)
      })
    },
    [logoutAsync]
  )

  const actionsValue = useMemo<AuthActions>(
    () => ({
      logout,
    }),
    [logout]
  )

  return (
    <AuthStateContext.Provider value={stateValue}>
      <AuthActionsContext.Provider value={actionsValue}>{children}</AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  )
}
